// src/components/microphone/MicrophoneCard.js
import React, { useContext, useRef, useEffect, useState } from 'react';
import { FaMicrophone, FaInfoCircle, FaPlay, FaStop, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import './MicrophoneCard.css';

const MicrophoneCard = ({ mic, isActive, isSelected, onActivate, onSelect }) => {
  const { audioStreamData } = useContext(AppContext);
  const canvasRef = useRef(null);
  const audioData = audioStreamData[mic.id];
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Draw waveform visualization whenever audio data is available
  useEffect(() => {
    if (!canvasRef.current || !audioData || !audioData.waveform) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Draw waveform
    const waveform = audioData.waveform;
    const sliceWidth = width / waveform.length;
    
    // Active or has recent data - draw in green
    ctx.beginPath();
    ctx.strokeStyle = isActive ? '#4CAF50' : '#AAAAAA';
    ctx.lineWidth = 2;
    
    let x = 0;
    for (let i = 0; i < waveform.length; i++) {
      const y = (waveform[i] * height / 2) + (height / 2);
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
      
      x += sliceWidth;
    }
    
    ctx.stroke();
  }, [audioData, mic.id, isActive]);
  
  // Create refs for persistent audio context and processor
  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);
  const processorIntervalRef = useRef(null);
  
  // Track active audio sources for proper cleanup
  const activeSourcesRef = useRef([]);
  
  // Handle audio playback with improved continuous playback
  useEffect(() => {
    // Initialize Web Audio API if it doesn't exist
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        gainNodeRef.current = audioContextRef.current.createGain();
        gainNodeRef.current.gain.value = 0.5; // 50% volume
        gainNodeRef.current.connect(audioContextRef.current.destination);
      } catch (err) {
        console.error('Failed to initialize audio context:', err);
        return;
      }
    }
    
    // Setup continuous audio playback when isPlaying changes
    if (isPlaying) {
      // Resume audio context if suspended
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(err => {
          console.error('Failed to resume audio context:', err);
        });
      }
      
      // Set up interval to continuously process new audio data
      processorIntervalRef.current = setInterval(() => {
        if (!audioData || !audioData.audio_data || !audioContextRef.current) return;
        
        try {
          const audioDataBase64 = audioData.audio_data;
          
          // Convert base64 to array buffer
          const binaryString = window.atob(audioDataBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Get Int16 samples with correct endianness
          const int16Samples = int16FromBytes(bytes.buffer);
          
          // Create AudioBuffer from audio data
          const sampleRate = audioData.format?.sampleRate || 22050;
          const buffer = audioContextRef.current.createBuffer(1, int16Samples.length, sampleRate);
          
          // Fill the buffer with the float data
          const channelData = buffer.getChannelData(0);
          for (let i = 0; i < int16Samples.length; i++) {
            // Convert from 16-bit int to float (-1.0 to 1.0) with slight amplification
            let sample = int16Samples[i] / 32768.0 * 1.2; // Amplify by 20%
            
            // Apply simple noise gate to reduce background noise
            if (Math.abs(sample) < 0.01) { // Threshold for noise gate
              sample = 0;
            }
            
            // Apply to channel
            channelData[i] = sample;
          }
          
          // Create and play the source
          const source = audioContextRef.current.createBufferSource();
          source.buffer = buffer;
          source.connect(gainNodeRef.current);
          
          // Keep track of active sources for cleanup
          activeSourcesRef.current.push(source);
          
          // Start playback
          source.start();
          
          // Auto-cleanup when this source ends
          source.onended = () => {
            if (source) {
              try {
                source.disconnect();
              } catch (e) {
                // Ignore disconnect errors
              }
              // Remove from active sources
              activeSourcesRef.current = activeSourcesRef.current.filter(s => s !== source);
            }
          };
        } catch (error) {
          console.error('Error processing audio data:', error);
        }
      }, 100); // Process new audio data every 100ms
    } else {
      // Stop all active sources when toggling off
      stopAllAudioSources();
      
      // Stop processing when not playing
      if (processorIntervalRef.current) {
        clearInterval(processorIntervalRef.current);
        processorIntervalRef.current = null;
      }
    }
    
    // Cleanup function
    return () => {
      if (processorIntervalRef.current) {
        clearInterval(processorIntervalRef.current);
        processorIntervalRef.current = null;
      }
      
      // Stop any playing sources when effect is cleaned up
      stopAllAudioSources();
    };
  }, [isPlaying, audioData]);
  
  // Helper function to stop all audio sources
  const stopAllAudioSources = () => {
    // Stop all active sources
    activeSourcesRef.current.forEach(source => {
      try {
        source.onended = null;
        source.stop();
        source.disconnect();
      } catch (e) {
        // Ignore errors on cleanup
      }
    });
    activeSourcesRef.current = [];
  };
  
  // Clean up audio context when component unmounts
  useEffect(() => {
    return () => {
      if (processorIntervalRef.current) {
        clearInterval(processorIntervalRef.current);
        processorIntervalRef.current = null;
      }
      
      // Safely disconnect gain node if it exists
      if (gainNodeRef.current) {
        try {
          gainNodeRef.current.disconnect();
        } catch (err) {
          console.log('Gain node already disconnected');
        }
        gainNodeRef.current = null;
      }
      
      // Safely close audio context if it exists and isn't closed
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close();
        } catch (err) {
          console.log('AudioContext already closed');
        }
        audioContextRef.current = null;
      }
    };
  }, []);
  
  // Helper function to convert byte array to Int16Array
  const int16FromBytes = (buffer) => {
    const view = new DataView(buffer);
    const samples = new Int16Array(buffer.byteLength / 2);
    
    for (let i = 0; i < samples.length; i++) {
      // Read 16-bit sample (little-endian)
      samples[i] = view.getInt16(i * 2, true);
    }
    
    return samples;
  };
  
  // Get activity status text
  const getStatusText = () => {
    if (mic.status === 'active') {
      return 'Active';
    } else if (audioData && audioData.timestamp) {
      // Format timestamp to "X ago"
      const timestamp = new Date(audioData.timestamp);
      const now = new Date();
      const diff = Math.floor((now - timestamp) / 1000); // difference in seconds
      
      if (diff < 60) return `${diff}s ago`;
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      return `${Math.floor(diff / 3600)}h ago`;
    }
    return 'Inactive';
  };
  
  return (
    <div 
      className={`microphone-card ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
    >
      <div className="mic-info" onClick={onSelect}>
        <div className="mic-icon">
          <FaMicrophone className="mic-icon-svg" />
        </div>
        <div className="mic-details">
          <h3>{mic.name}</h3>
          <p className="mic-location">{mic.location || 'Unknown'}</p>
        </div>
        <div className="mic-status">
          <span className={`status-indicator ${mic.status === 'active' ? 'active' : 'inactive'}`}>
            {getStatusText()}
          </span>
        </div>
      </div>
      
      <div className="waveform-container">
        <canvas 
          ref={canvasRef}
          className="waveform-canvas" 
          width="300" 
          height="100"
        ></canvas>
        
        {!audioData && (
          <div className="waveform-placeholder">
            <p>Waiting for audio data...</p>
          </div>
        )}
      </div>
      
      <div className="mic-controls">
        {audioData && audioData.audio_data && (
          <button 
            className={`audio-button ${isPlaying ? 'playing' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsPlaying(!isPlaying);
            }}
          >
            {isPlaying ? <FaVolumeMute className="control-icon" /> : <FaVolumeUp className="control-icon" />}
            <span>{isPlaying ? 'Mute' : 'Listen'}</span>
          </button>
        )}
        
        <button 
          className="details-button"
          onClick={onSelect}
        >
          <FaInfoCircle className="control-icon" />
          <span>Details</span>
        </button>
      </div>
    </div>
  );
};

export default MicrophoneCard;
